<template>
  <div class="blytz-datatable">
    <div v-if="title" class="header">
      <div class="title">{{ title }}</div>
    </div>
    <div v-if="actions.length > 0 || filters.length > 0" class="actions">
      <div class="filter-group d-flex">
        <div v-for="filter in filters" :key="filter.label">
          <div v-if="filter.type === 'date'">
            <label>Date Range</label>
            <BlytzDatePicker
              :start-date="filter.props.startDate.toString()"
              :end-date="filter.props.endDate.toString()"
              :on-date-change="filter.onFilter"
            />
          </div>
          <SwitchInput
            v-else-if="filter.type === 'toggle'"
            :label="filter.label"
            @change="filter.onFilter"
          />
          <div v-else-if="filter.type === 'select'">
            <label>{{ filter.label }}</label>
            <b-form-select
              size="sm"
              :value="filter.props && filter.props.value"
              :options="filter.props && filter.props.options"
              @change="filter.onFilter"
            />
          </div>
          <div v-else-if="filter.type === 'search'">
            <label>{{ filter.label }}</label>
            <b-form-input
              size="sm"
              :value="filter.props && filter.props.value"
              :placeholder="filter.props && filter.props.placeholder"
              @change="filter.onFilter"
            />
          </div>
          <b-dropdown
            v-else-if="filter.type === 'dropdown'"
            :text="filter.label"
            size="sm"
            variant="outline-blue"
            menu-class="filter-dropdown-menu"
          >
            <b-dropdown-form>
              <b-form-checkbox
                v-for="option in filter.props.options"
                :key="option.value"
                v-model="option.selected"
                class="mb-3"
                @change="filter.onFilter"
              >
                {{ option.label }}
              </b-form-checkbox>
            </b-dropdown-form>
          </b-dropdown>
        </div>
      </div>
      <div class="actions-group">
        <b-button
          v-for="action in actions"
          :key="action.label"
          :variant="
            action.variant || (action.outline ? 'outline-blue' : 'blue')
          "
          :disabled="action.requireSelection && selected.length === 0"
          @click="() => action.action(selected)"
        >
          {{ action.label }}
        </b-button>
      </div>
    </div>

    <b-table
      :fields="fields"
      :items="tableData.items"
      :striped="striped"
      :hover="isClickable"
      :busy="busy"
      :selectable="selectable"
      :no-local-sorting="noLocalSorting"
      :fixed="fixedLayout"
      th-class="table-header-column"
      :class="[className, { 'row-clickable': isClickable }]"
      :tbody-tr-class="rowClass"
      @sort-changed="onSortChanged"
      @row-selected="onRowSelected"
      @row-clicked="onRowClicked"
    >
      <template #table-busy>
        <div class="table-busy-state">
          <b-spinner small />
          <span>Loading…</span>
        </div>
      </template>

      <template #head(checkbox)="{ selectAllRows, clearSelected }">
        <input
          type="checkbox"
          :checked="allRowsSelected"
          @click="() => (allRowsSelected ? clearSelected() : selectAllRows())"
        />
      </template>
      <template #cell(checkbox)="{ rowSelected, selectRow, unselectRow }">
        <input
          type="checkbox"
          :checked="rowSelected"
          @click.stop="rowSelected ? unselectRow() : selectRow()"
        />
      </template>

      <template v-if="onShowDetails" #cell(details)="{ item }">
        <button class="info-button" @click.stop="() => onShowDetails(item)">
          <InfoIcon />
        </button>
      </template>

      <template v-for="(_, slotName) of $scopedSlots" #[slotName]="scope">
        <slot :name="slotName" v-bind="scope" />
      </template>
    </b-table>

    <DataTablePagination
      v-if="paginated"
      :on-page-size-change="onPageSizeChange"
      :on-page-change="onPageChange"
      :total-records="totalRecords"
      :page-size="pageSize"
      :current-page="currentPage"
      :page-size-options="pageSizeOptions"
    />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import BlytzDatePicker from '@/components/BlytzDatePicker/BlytzDatePicker.vue'
import SwitchInput from '@/components/SwitchInput/SwitchInput.vue'
import DataTablePagination from '@/components/DataTable/DataTablePagination.vue'
import InfoIcon from '@/Icons/InfoIcon.vue'

export interface Action {
  label: string
  outline?: boolean
  variant?: string
  action: (selected: any) => void
  requireSelection: boolean
}

export interface TableData {
  headers: { key: string; label: string }[]
  items: { [key: string]: string }[]
}

export interface Filter {
  label: string
  type: 'date' | 'toggle' | 'select' | 'search' | 'dropdown'
  props?: any
  onFilter: (value: any) => void
}

export default Vue.extend({
  name: 'DataTable',
  components: {
    DataTablePagination,
    InfoIcon,
    BlytzDatePicker,
    SwitchInput
  },
  props: {
    selectable: {
      type: Boolean,
      default: false
    },
    showDetails: {
      type: Boolean,
      default: false
    },
    onShowDetails: {
      type: Function,
      default: () => false
    },
    title: {
      type: String,
      default: ''
    },
    actions: {
      type: Array as () => Action[],
      default: (selected: any) => [] as Action[]
    },
    onSortChanged: {
      type: Function,
      default: () => false
    },
    // b-table only ever sees the page it was handed, so its own sort reorders
    // that page and nothing else. Callers that paginate a full client-side list
    // set this and sort the list themselves in onSortChanged.
    noLocalSorting: {
      type: Boolean,
      default: false
    },
    // Auto layout sizes each column to whatever the current rows hold, so a filter that changes the
    // content also moves every column. Callers whose rows change under a filter set this and give their
    // headers explicit widths via `thStyle`.
    fixedLayout: {
      type: Boolean,
      default: false
    },
    onRowClicked: {
      type: Function,
      required: false,
      default: undefined
    },
    // Passed straight to b-table's tbody-tr-class; callers use it to tint
    // rows by state (e.g. resolved autopay failures).
    rowClass: {
      type: [String, Function],
      required: false,
      default: undefined
    },
    striped: {
      type: Boolean,
      default: true
    },
    busy: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Array as () => Filter[],
      default: () => [] as Filter[]
    },
    hasDateFilter: {
      type: Boolean,
      default: false
    },
    onDateFilter: {
      type: Function,
      default: (dateRange: string) => false
    },
    tableData: {
      type: Object as () => TableData,
      default: () => ({ headers: [], items: [] })
    },
    onPageChange: {
      type: Function,
      default: (page: number) => false
    },
    onPageSizeChange: {
      type: Function,
      default: (size: number) => false
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 10
    },
    pageSizeOptions: {
      type: Array,
      default: () => [10, 20, 50]
    },
    totalRecords: {
      type: Number,
      default: 0
    },
    paginated: {
      type: Boolean,
      default: true
    },
    className: {
      type: String,
      default: ''
    }
  },
  data(): {
    selected: any[]
    startDate: Date
    endDate: Date
  } {
    return {
      selected: [] as any[],
      // start date 7 days ago. End date today
      startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
      endDate: new Date()
    }
  },
  computed: {
    fields(): { key: string; label: string }[] {
      const { headers } = this.tableData as TableData
      this.showDetails && headers.push({ key: 'details', label: 'Details' })
      this.selectable && headers.unshift({ key: 'checkbox', label: '' })

      return headers
    },
    allRowsSelected(): boolean {
      return (
        (this.selected as []).length ===
        (this.tableData as TableData).items.length
      )
    },
    isClickable(): boolean {
      return typeof this.onRowClicked === 'function'
    }
  },
  methods: {
    onRowSelected(items: any[]) {
      this.$data.selected = items
    },
    dateChange(e) {
      console.log('change', e.target.checked)
    }
  }
})
</script>

<style lang="scss">
/* This CSS should be scoped, but we are not using scoped styles because of a problem that prevents styling table headers */
.blytz-datatable {
  margin: 24px 0;

  border-radius: 8px;
  thead > tr > th {
    vertical-align: middle;
  }
  .header {
    display: flex;
    justify-content: space-between;
    padding: 12px;
  }
  .title {
    margin-bottom: 1rem;

    color: #5f3effbf;
  }
  .info-button {
    background: none;
    border: none;
  }
  .actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 1rem;
  }
  .filters {
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 0 12px 12px;
  }
  .actions-group {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    height: fit-content;
  }
  .row-clickable tbody tr {
    cursor: pointer;
  }
  .filter-group {
    gap: 0.75rem;
    align-items: flex-end;
  }
  .filter-dropdown-menu {
    width: 11rem;
  }
  .table-busy-state {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    padding: 2rem 0;

    color: #6c757d;
  }
}
</style>
